import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Paper, 
  Title, 
  Table, 
  Button, 
  Alert, 
  Loader, 
  Group, 
  Badge, 
  Radio,
  Stack,
  Text,
  ScrollArea,
  ActionIcon,
  Tooltip
} from '@mantine/core';
import { 
  IconRefresh, 
  IconDeviceFloppy, 
  IconHome, 
  IconCheck,
  IconX,
  IconClock,
  IconAlertTriangle
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import './HousekeepingCleanStatus.css';

const HousekeepingCleanStatus = () => {
  const [units, setUnits] = useState([]);
  const [cleanStatuses, setCleanStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [changes, setChanges] = useState({});

  // Available clean statuses from the database (will be populated from API)
  const [cleanStatusOptions, setCleanStatusOptions] = useState([]);

  useEffect(() => {
    fetchUnits();
    fetchCleanStatuses();
  }, []);

  const fetchUnits = async () => {
    try {
      const response = await fetch('/api/units');
      if (!response.ok) throw new Error('Failed to fetch units');
      const data = await response.json();
      
      // Extract units from response (API returns {units: [...], pagination: {...}})
      console.log('API response data:', data);
      console.log('Data type:', typeof data);
      
      if (data && data.units && Array.isArray(data.units)) {
        setUnits(data.units);
        console.log('Units extracted successfully:', data.units.length, 'units');
      } else if (Array.isArray(data)) {
        // Fallback: if data is directly an array
        setUnits(data);
        console.log('Data is directly an array:', data.length, 'units');
      } else {
        console.error('Unexpected data format:', data);
        setError('Invalid data format received from server');
        setUnits([]);
      }
    } catch (err) {
      console.error('Error fetching units:', err);
      setError('Error fetching units: ' + err.message);
      setUnits([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCleanStatuses = async () => {
    try {
      const response = await fetch('/api/clean-statuses');
      if (!response.ok) throw new Error('Failed to fetch clean statuses');
      const data = await response.json();
      setCleanStatuses(data);
      
      // Transform the API data into the format expected by the component
      const transformedOptions = data.map(status => ({
        code: status.code,
        description: status.description,
        color: getStatusColor(status.code),
        sort_order: status.sort_order
      }));
      
      // Sort by sort_order
      transformedOptions.sort((a, b) => a.sort_order - b.sort_order);
      setCleanStatusOptions(transformedOptions);
      
    } catch (err) {
      console.error('Error fetching clean statuses:', err);
    }
  };

  const handleStatusChange = (unitId, newStatus) => {
    setChanges(prev => ({
      ...prev,
      [unitId]: newStatus
    }));
  };

  const saveChanges = async () => {
    if (Object.keys(changes).length === 0) {
      setError('No changes to save');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const updatePromises = Object.entries(changes).map(([unitId, cleanStatus]) =>
        fetch(`/api/units/${unitId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ clean_status: cleanStatus })
        })
      );

      await Promise.all(updatePromises);
      
      // Update local state
      setUnits(prevUnits => 
        prevUnits.map(unit => 
          changes[unit.id] ? { ...unit, clean_status: changes[unit.id] } : unit
        )
      );

      setChanges({});
      
      // Show success notification
      notifications.show({
        title: 'Success!',
        message: 'All clean status changes saved successfully!',
        color: 'green',
        icon: <IconCheck size="1rem" />,
      });
      
      // Refresh data
      setTimeout(() => {
        fetchUnits();
      }, 1000);

    } catch (err) {
      setError('Error saving changes: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status) => {
    // Map status codes to Mantine color classes
    const colorMap = {
      'CLEAN': 'green',
      'DIRTY': 'red',
      'IN_PROGRESS': 'yellow',
      'INSPECTED': 'blue',
      'MAINTENANCE': 'gray',
      'OUT_OF_ORDER': 'dark',
      'TURNOVER': 'violet',
      'DEEP_CLEAN': 'grape'
    };
    return colorMap[status] || 'gray';
  };

  const getStatusDescription = (status) => {
    const statusOption = cleanStatusOptions.find(opt => opt.code === status);
    return statusOption ? statusOption.description : status;
  };

  const hasChanges = Object.keys(changes).length > 0;

  if (loading) {
    return (
      <Container size="xl" py="xl">
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text size="lg" c="dimmed">Loading units...</Text>
        </Stack>
      </Container>
    );
  }

    return (
    <Container size="xl" py="xl">
      <Paper shadow="sm" p="xl" radius="md">
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="center">
            <Group gap="sm">
              <IconHome size="2rem" color="var(--mantine-color-blue-6)" />
              <Title order={2} c="dark">Housekeeping Clean Status</Title>
            </Group>
            
            <Group gap="sm">
              {hasChanges && (
                <Button 
                  color="green" 
                  onClick={saveChanges}
                  loading={saving}
                  leftSection={<IconDeviceFloppy size="1rem" />}
                >
                  Save Changes
                </Button>
              )}
              <Button 
                variant="outline" 
                onClick={fetchUnits}
                leftSection={<IconRefresh size="1rem" />}
              >
                Refresh
              </Button>
            </Group>
          </Group>

          {/* Alerts */}
          {error && (
            <Alert 
              color="red" 
              title="Error" 
              icon={<IconX size="1rem" />}
              onClose={() => setError(null)}
              withCloseButton
            >
              {error}
            </Alert>
          )}

          {/* Table */}
          <ScrollArea>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Unit #</Table.Th>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Zone</Table.Th>
                  <Table.Th>Current Status</Table.Th>
                  {cleanStatusOptions.map(status => (
                    <Table.Th key={status.code} ta="center">
                      <Badge 
                        color={getStatusColor(status.code)} 
                        variant="light"
                        size="lg"
                      >
                        {status.code}
                      </Badge>
                    </Table.Th>
                  ))}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {Array.isArray(units) && units.length > 0 ? (
                  units.map(unit => (
                    <Table.Tr key={unit.id} bg={changes[unit.id] ? 'yellow.0' : undefined}>
                      <Table.Td>
                        <Stack gap="xs">
                          <Text fw={600} size="lg">{unit.unit_number}</Text>
                          {changes[unit.id] && (
                            <Text size="xs" c="dimmed">
                              Changing to: {getStatusDescription(changes[unit.id])}
                            </Text>
                          )}
                        </Stack>
                      </Table.Td>
                      <Table.Td>{unit.unit_type_name || 'N/A'}</Table.Td>
                      <Table.Td>{unit.cleaning_zone || 'N/A'}</Table.Td>
                      <Table.Td>
                        <Badge 
                          color={getStatusColor(unit.clean_status)} 
                          variant="filled"
                          size="md"
                        >
                          {unit.clean_status}
                        </Badge>
                      </Table.Td>
                      {cleanStatusOptions.map(status => (
                        <Table.Td key={status.code} ta="center">
                          <Radio
                            name={`unit-${unit.id}`}
                            checked={
                              changes[unit.id] 
                                ? changes[unit.id] === status.code
                                : unit.clean_status === status.code
                            }
                            onChange={() => handleStatusChange(unit.id, status.code)}
                            color={getStatusColor(status.code)}
                          />
                        </Table.Td>
                      ))}
                    </Table.Tr>
                  ))
                ) : (
                  <Table.Tr>
                    <Table.Td colSpan={cleanStatusOptions.length + 4} ta="center">
                      <Text c="dimmed" size="lg">No units found</Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </ScrollArea>

          {/* Pending Changes */}
          {hasChanges && (
            <Paper p="md" bg="blue.0" radius="md">
              <Title order={4} mb="sm">Pending Changes</Title>
              <Stack gap="xs">
                {Object.entries(changes).map(([unitId, newStatus]) => {
                  const unit = units.find(u => u.id === parseInt(unitId));
                  return (
                    <Text key={unitId} size="sm">
                      Unit {unit?.unit_number}: {getStatusDescription(unit?.clean_status)} → {getStatusDescription(newStatus)}
                    </Text>
                  );
                })}
              </Stack>
            </Paper>
          )}
        </Stack>
      </Paper>
    </Container>
  );
};

export default HousekeepingCleanStatus;
